import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Placeholder, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { buildSiteDetailJsonLd, SiteSettingsLike } from 'components/SiteMaster/siteDetailJsonLd';
import {
  useSiteMasterRouteFields,
  useSiteRecruitingStudiesProps,
} from 'components/SiteMaster/utils';
import {
  StSiteMasterDetailColumn,
  StSiteMasterDetailGrid,
  StSiteMasterDetailWrapper,
  StSiteMasterSidebarColumn,
} from 'components/SiteMaster/SiteMaster.styles';

const SiteDetail = (props: ComponentProps): JSX.Element => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { sitecoreContext } = useSitecoreContext();
  const routeFields = useSiteMasterRouteFields();
  const { studies } = useSiteRecruitingStudiesProps();
  const router = useRouter();

  const renderJSONLDSchema = (): JSX.Element => {
    const siteSettings = sitecoreContext?.siteSettings as SiteSettingsLike;
    const baseUrl = siteSettings?.baseUrl?.value?.trim() || '';
    const path =
      (sitecoreContext?.itemPath as string | undefined) || router?.asPath?.split(/[?#]/)[0] || '';
    // Schema.org url must be absolute; omit when origin or path is unavailable.
    const pageUrl =
      baseUrl && path
        ? `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`.replace(
            /([^:]\/)\/+/g,
            '$1'
          )
        : undefined;

    const schemaData = buildSiteDetailJsonLd(routeFields, siteSettings, studies, pageUrl);

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
    );
  };

  return (
    <>
      {renderJSONLDSchema()}
      <StSiteMasterDetailGrid id="site-details-container">
        {isDesktop ? (
          <StSiteMasterDetailWrapper id="site-details-wrapper">
            <Box id="site-details-left">
              <StSiteMasterDetailColumn>
                <Placeholder
                  name="jss-site-left"
                  rendering={props.rendering}
                  disableSuspense={true}
                />
              </StSiteMasterDetailColumn>
            </Box>

            <Box id="site-details-right">
              <StSiteMasterSidebarColumn>
                <Placeholder
                  name="jss-site-right"
                  rendering={props.rendering}
                  disableSuspense={true}
                />
              </StSiteMasterSidebarColumn>
            </Box>
          </StSiteMasterDetailWrapper>
        ) : (
          <StSiteMasterDetailColumn>
            <Placeholder
              name="jss-site-left"
              rendering={props.rendering}
              id="site-details-sm-left"
              disableSuspense={true}
            />

            <Placeholder
              name="jss-site-right"
              rendering={props.rendering}
              id="site-details-sm-right"
              disableSuspense={true}
            />
          </StSiteMasterDetailColumn>
        )}
      </StSiteMasterDetailGrid>
    </>
  );
};

export default SiteDetail;
